import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useLunaStore from '../../store/useLunaStore';
import MessagePanel from '../common/MessagePanel';
import api from '../../services/api';
import { getUserFullTitle } from '../../utils/userTitles';
import DevSetupModal from '../common/DevSetupModal';
import LunaGuideModal from '../common/LunaGuideModal';

export default function Header({ onNavigate, onNewTopic, onLogout, onPasswordChange, onShowTerms, customAction, adminMode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isSoundOn, setIsSoundOn] = useState(true);
    // Message State
    const [messages, setMessages] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const [isMessagePanelOpen, setIsMessagePanelOpen] = useState(false);
    const [isDevSetupOpen, setIsDevSetupOpen] = useState(false);
    const [isLunaGuideOpen, setIsLunaGuideOpen] = useState(false);

    const { username, nickname, userLevel, userXP, requiredXP, isAdmin, learnedLanguages } = useLunaStore();
    const profileRef = useRef(null);

    const [isEditingNickname, setIsEditingNickname] = useState(false);
    const [newNickname, setNewNickname] = useState('');

    const handleUpdateNickname = async () => {
        if (!newNickname || newNickname.trim().length < 2) {
            alert('닉네임을 2자 이상 입력해주세요.');
            return;
        }
        try {
            const data = await api.updateProfile(newNickname);
            // Manually update store to reflect new nickname immediately
            const currentStore = useLunaStore.getState();
            currentStore.setUserSession({
                username: currentStore.username,
                nickname: data.user.nickname,
                is_admin: currentStore.isAdmin,
                user_id: currentStore.userId,
                level: currentStore.userLevel,
                total_xp: currentStore.userXP,
                streak: currentStore.userStreak,
                required_xp: currentStore.requiredXP
            });
            setIsEditingNickname(false);
        } catch (err) {
            console.error(err);
            alert('닉네임 변경 실패: ' + (err.message || '알 수 없는 오류'));
        }
    };

    // Fallback navigation handler
    const handleNavigate = (path) => {
        if (onNavigate) {
            onNavigate(path);
        } else {
            navigate(path);
        }
    };

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setIsProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load messages on mount (only if logged in)
    useEffect(() => {
        if (username) {
            loadMessages();
        }
    }, [username]);

    const loadMessages = async () => {
        try {
            const data = await api.getUserMessages();
            setMessages(data?.messages || []);
            setUnreadCount(data?.unread_count || 0);
        } catch (err) {
            console.warn('[Header] Failed to load messages:', err);
        }
    };

    const handleMarkAsRead = async (messageId) => {
        try {
            await api.markMessageAsRead(messageId);
            setMessages(prev => prev.map(msg =>
                msg.id === messageId ? { ...msg, is_read: true } : msg
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.warn('[Header] Failed to mark message as read:', err);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await api.markAllMessagesAsRead();
            setMessages(prev => prev.map(msg => ({ ...msg, is_read: true })));
            setUnreadCount(0);
        } catch (err) {
            console.warn('[Header] Failed to mark all as read:', err);
        }
    };

    // Toggle Sound
    const toggleSound = () => {
        setIsSoundOn(!isSoundOn);
        // Here you would typically toggle a global sound context or mute the window
    };

    const handleMessageClick = () => {
        setIsMessagePanelOpen(true);
    };

    return (
        <header className="flex items-center justify-between px-6 pt-4 pb-0 mb-0 bg-transparent border-0 shrink-0 h-16 z-50 sticky top-0 transition-all duration-300">
            <div className="flex items-center gap-6">
                {/* L.U.N.A. Brand Identity - With Full Name */}
                <div onClick={() => handleNavigate('/')} className="flex items-center gap-3 select-none cursor-pointer group">
                    {/* Main Logo */}
                    <h1 className="text-4xl font-black tracking-tight leading-none font-sans">
                        <span className="text-white group-hover:text-slate-100 transition-colors">LU</span>
                        <span className="text-purple-500 group-hover:text-purple-400 transition-colors">NA</span>
                    </h1>
                    {/* Full Name - Matched Height with Logo */}
                    <div className="flex flex-col justify-center leading-tight">
                        <span className="text-[11px] font-bold text-slate-400 tracking-wide">Logic Unified</span>
                        <span className="text-[11px] font-bold text-purple-400/80 tracking-wide">Neural Adviser</span>
                    </div>
                </div>
            </div>

            {/* Center: Copyright & Info - Simplified without Pill Background */}
            {/* Center: Copyright & Info & Dev Setup - Unified Left Group */}
            <div className="hidden lg:flex items-center gap-8 text-[12px] text-slate-500 font-medium">
                <button
                    onClick={onShowTerms}
                    className="group flex items-center gap-2 text-[11px] text-slate-600 hover:text-slate-300 transition-colors uppercase tracking-widest font-semibold cursor-pointer bg-transparent border-0 p-0"
                >
                    <span>© 2026 Seyoong Jang</span>
                    <span className="w-px h-2.5 bg-slate-700 group-hover:bg-slate-500 transition-colors"></span>
                    <span>LUNA - Logic Unified Neural Advisor</span>
                </button>

                {/* Guide & Dev Setup Buttons - Grouped Together */}
                <div className="hidden lg:flex items-center gap-2 relative z-50">
                    {/* LUNA Guide Button */}
                    <button
                        onClick={() => setIsLunaGuideOpen(true)}
                        className="group/guide relative flex items-center gap-2 px-4 py-2 rounded-full bg-slate-200 text-slate-900 font-bold hover:bg-white active:scale-95 transition-all duration-200 shadow-[0_0_15px_rgba(255,255,255,0.05)] border border-transparent"
                    >
                        <i className="fas fa-compass text-xs opacity-70 group-hover/guide:opacity-100 transition-opacity"></i>
                        <span className="text-[11px]">LUNA의 활용법</span>

                        {/* Tooltip */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-max px-3 py-1.5 bg-slate-800/90 text-slate-200 text-[10px] rounded-lg opacity-0 group-hover/guide:opacity-100 group-hover/guide:translate-y-0 translate-y-2 transition-all duration-300 pointer-events-none backdrop-blur-md border border-white/10 shadow-xl flex flex-col items-center">
                            <div className="absolute -top-1 w-2 h-2 bg-slate-800/90 border-t border-l border-white/10 rotate-45 transform"></div>
                            <span className="relative z-10">LUNA를 200% 활용하는 방법!</span>
                        </div>
                    </button>

                    {/* Dev Setup Button */}
                    <button
                        onClick={() => setIsDevSetupOpen(true)}
                        className="group/dev relative flex items-center gap-2 px-4 py-2 rounded-full bg-slate-200 text-slate-900 font-bold hover:bg-white active:scale-95 transition-all duration-200 shadow-[0_0_15px_rgba(255,255,255,0.05)] border border-transparent"
                    >
                        <i className="fas fa-terminal text-xs opacity-70 group-hover/dev:opacity-100 transition-opacity"></i>
                        <span className="text-[11px]">개발환경 세팅</span>

                        {/* Tooltip */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-max px-3 py-1.5 bg-slate-800/90 text-slate-200 text-[10px] rounded-lg opacity-0 group-hover/dev:opacity-100 group-hover/dev:translate-y-0 translate-y-2 transition-all duration-300 pointer-events-none backdrop-blur-md border border-white/10 shadow-xl flex flex-col items-center">
                            <div className="absolute -top-1 w-2 h-2 bg-slate-800/90 border-t border-l border-white/10 rotate-45 transform"></div>
                            <span className="relative z-10">진짜 내 PC에서 개발해보고 싶다면?</span>
                        </div>
                    </button>
                </div>
            </div>


            {/* Right: Actions & User */}
            <div className="flex items-center gap-3">


                {/* Navigation Group - Primary Focus */}
                <div className="flex items-center gap-2">
                    {(customAction?.props?.adminMode || window.location.pathname.startsWith('/admin')) ? (
                        // Admin Mode Navigation
                        [
                            { path: '/admin', label: '관리자 페이지', icon: 'fa-users-cog', color: 'cyan', gradient: 'from-cyan-600 to-cyan-500' },
                            { path: '/admin/analytics', label: '인사이트 보드', icon: 'fa-chart-pie', color: 'indigo', gradient: 'from-indigo-600 to-indigo-500' }
                        ].map((item) => (
                            <button
                                key={item.path}
                                onClick={() => handleNavigate(item.path)}
                                className={`
                                    relative flex items-center gap-2 px-4 py-2 h-9 rounded-full transition-all duration-300 group
                                    ${location.pathname === item.path
                                        ? `bg-gradient-to-r ${item.gradient} text-white shadow-[0_0_15px_rgba(6,182,212,0.4)]`
                                        : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'}
                                    active:scale-95 border border-transparent
                                `}
                            >
                                <i className={`fas ${item.icon} text-xs ${location.pathname === item.path ? 'text-white' : 'text-slate-400 group-hover:text-white'} transition-colors`}></i>
                                <span className="text-[11px] font-bold">{item.label}</span>
                            </button>
                        ))
                    ) : (
                        // Standard User Navigation
                        ['/dashboard', '/roadmap', '/leaderboard'].map((path, idx) => {
                            const labels = ['대시보드', '로드맵', '랭킹'];
                            // const icons = ['fa-chart-line', 'fa-map', 'fa-trophy']; // Icons removed for cleaner look

                            // Locked state for Guest
                            if (!username) {
                                return (
                                    <div key={path} className="relative group/lock px-1">
                                        <button disabled className="text-xs font-medium text-slate-600 cursor-not-allowed hover:text-slate-500 transition-colors px-3 py-2">
                                            {labels[idx]}
                                        </button>
                                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover/lock:opacity-100 transition-opacity pointer-events-none">
                                            <i className="fas fa-lock text-[10px] text-slate-500"></i>
                                        </div>
                                    </div>
                                );
                            }

                            const isActive = location.pathname === path;
                            return (
                                <button
                                    key={path}
                                    onClick={() => handleNavigate(path)}
                                    className={`relative px-3 py-2 text-xs font-medium transition-all duration-200 group ${isActive ? 'text-white' : 'text-slate-400 hover:text-slate-200'}`}
                                >
                                    <span>{labels[idx]}</span>
                                    {isActive && (
                                        <motion.div
                                            layoutId="navParams"
                                            className="absolute bottom-0 left-0 w-full h-0.5 bg-cyan-500 rounded-full"
                                        />
                                    )}
                                </button>
                            );
                        })
                    )}
                </div>

                {/* Utilities Group - Minimal */}
                <div className="flex items-center gap-4 mx-2">
                    {/* Message Button - Locked for Guest */}
                    {!username ? (
                        <div className="relative group/lock opacity-50">
                            <i className="fas fa-envelope text-slate-600 text-sm"></i>
                        </div>
                    ) : (
                        <button id="message-icon-btn" onClick={handleMessageClick} className="relative text-slate-400 hover:text-white transition-colors group">
                            <i className="fas fa-envelope text-sm group-hover:scale-110 transition-transform"></i>
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-red-500 ring-2 ring-[#0B0E14] animate-pulse"></span>
                            )}
                        </button>
                    )}

                    <button onClick={toggleSound} className="text-slate-400 hover:text-white transition-colors group">
                        {isSoundOn ? <i className="fas fa-volume-up text-sm group-hover:scale-110 transition-transform"></i> : <i className="fas fa-volume-mute text-sm group-hover:scale-110 transition-transform"></i>}
                    </button>
                </div>

                {/* Custom Action (e.g., Reset Roadmap) */}
                {customAction && (
                    <div className="mr-2">
                        {customAction}
                    </div>
                )}

                {/* Primary Action / Back Button */}
                {!['/', '/app'].includes(location.pathname) ? (
                    <button onClick={() => navigate('/app')} className="relative flex items-center gap-2 px-4 py-2 rounded-full bg-slate-200 text-slate-900 font-bold hover:bg-white active:scale-95 transition-all duration-200 group shadow-[0_0_15px_rgba(255,255,255,0.05)] border border-transparent">
                        <i className="fas fa-arrow-left text-xs group-hover:-translate-x-1 transition-transform duration-300"></i>
                        <span className="text-[11px]">학습으로 돌아가기</span>
                    </button>
                ) : (
                    <button
                        onClick={username ? onNewTopic : () => alert('로그인 후 새로운 주제를 시작할 수 있습니다.')}
                        className={`relative flex items-center gap-2 px-4 py-2 rounded-full font-bold transition-all duration-200 group shadow-[0_0_15px_rgba(255,255,255,0.05)] border border-transparent ${username
                                ? 'bg-slate-200 text-slate-900 hover:bg-white active:scale-95'
                                : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                            }`}
                        title={username ? "새로운 주제 시작" : "로그인 필요"}
                    >
                        <i className={`fas fa-plus text-xs ${username ? 'group-hover:rotate-90' : ''} transition-transform duration-300`}></i>
                        <span className="text-[11px]">새 주제</span>
                    </button>
                )}

                {/* User Menu Trigger */}
                <div className="relative ml-2" ref={profileRef}>
                    <button
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="flex items-center gap-3 px-4 py-2 rounded-full bg-[#1e293b] hover:bg-slate-700 transition-colors active:scale-95 group"
                    >
                        {username ? (
                            <>
                                <i className="fas fa-user-circle text-indigo-400 text-sm"></i>
                                <span className="text-sm font-bold text-white group-hover:text-cyan-200 transition-colors">
                                    {nickname || username}
                                </span>
                            </>
                        ) : (
                            <>
                                <i className="fas fa-ghost text-slate-400 text-sm"></i>
                                <span className="text-sm font-medium text-slate-400 group-hover:text-slate-300 transition-colors">
                                    Guest
                                </span>
                            </>
                        )}
                        <i className={`fas fa-chevron-down text-[10px] ml-1 transition-transform duration-300 ${isProfileOpen ? 'rotate-180 text-white' : 'text-slate-400 group-hover:text-white'}`}></i>
                    </button>

                    {/* Dropdown - Unified Design with DevSetupModal */}
                    <AnimatePresence>
                        {isProfileOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                className="absolute right-0 top-full mt-3 w-72 bg-[#0f1219] border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden"
                            >
                                {/* Header Section */}
                                <div className="px-5 py-4 border-b border-white/5 bg-gradient-to-r from-[#0f1219] to-[#1e293b]">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-sm font-bold text-white shadow-lg">
                                            {username ? (nickname || username)[0].toUpperCase() : 'G'}
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0">
                                            {username ? (
                                                isEditingNickname ? (
                                                    <form
                                                        onSubmit={(e) => { e.preventDefault(); handleUpdateNickname(); }}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <input
                                                            type="text"
                                                            value={newNickname}
                                                            onChange={(e) => setNewNickname(e.target.value)}
                                                            className="w-full bg-slate-800/50 border border-slate-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-cyan-500"
                                                            placeholder="새 닉네임"
                                                            autoFocus
                                                        />
                                                        <button type="submit" className="text-cyan-400 hover:text-cyan-300 text-xs shrink-0">
                                                            <i className="fas fa-check"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setIsEditingNickname(false)}
                                                            className="text-slate-500 hover:text-slate-400 text-xs shrink-0"
                                                        >
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </form>
                                                ) : (
                                                    <div className="flex items-center gap-2 group/edit">
                                                        <span className="text-sm font-bold text-white truncate max-w-[140px]" title={nickname || username}>
                                                            {nickname || username}
                                                        </span>
                                                        <button
                                                            onClick={() => { setNewNickname(nickname || username); setIsEditingNickname(true); }}
                                                            className="text-slate-500 hover:text-cyan-400 opacity-0 group-hover/edit:opacity-100 transition-opacity"
                                                            title="닉네임 변경"
                                                        >
                                                            <i className="fas fa-pen text-[10px]"></i>
                                                        </button>
                                                    </div>
                                                )
                                            ) : (
                                                <span className="text-sm font-bold text-white">Guest User</span>
                                            )}
                                            <span className="text-[10px] text-slate-400">
                                                {username ? getUserFullTitle(userLevel, learnedLanguages || []) : 'Dreamer'}
                                            </span>
                                        </div>
                                    </div>

                                    {username && (
                                        <div className="mt-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                                                    Level {userLevel || 1}
                                                </span>
                                                <span className="text-xs text-cyan-400 font-mono font-medium">
                                                    {userXP || 0} / {requiredXP || 100} XP
                                                </span>
                                            </div>
                                            <div className="w-full h-1.5 bg-black/40 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-500"
                                                    style={{ width: `${Math.min(100, ((userXP || 0) / (requiredXP || 100)) * 100)}%` }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {!username && (
                                        <div className="mt-3 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                                            <p className="text-xs text-cyan-300 leading-relaxed text-center">
                                                로그인하고 학습 기록을 저장하세요
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Menu Items */}
                                <div className="p-2 space-y-0.5">
                                    <button onClick={() => handleNavigate('/dashboard')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-all group">
                                        <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                                            <i className="fas fa-chart-line text-sm text-cyan-400"></i>
                                        </div>
                                        <span className="font-medium">마이 대시보드</span>
                                    </button>
                                    <button onClick={() => handleNavigate('/roadmap')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-all group">
                                        <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                                            <i className="fas fa-map text-sm text-purple-400"></i>
                                        </div>
                                        <span className="font-medium">커리어 로드맵</span>
                                    </button>

                                    {isAdmin && (
                                        <>
                                            <div className="h-px bg-white/5 my-1 mx-3"></div>
                                            <button onClick={() => handleNavigate('/admin')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-all group">
                                                <div className="w-8 h-8 rounded-lg bg-pink-500/10 flex items-center justify-center group-hover:bg-pink-500/20 transition-colors">
                                                    <i className="fas fa-cog text-sm text-pink-400"></i>
                                                </div>
                                                <span className="font-medium">관리자 페이지</span>
                                            </button>
                                            <button onClick={() => handleNavigate('/admin/analytics')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-all group">
                                                <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center group-hover:bg-indigo-500/20 transition-colors">
                                                    <i className="fas fa-chart-pie text-sm text-indigo-400"></i>
                                                </div>
                                                <span className="font-medium">인사이트 보드</span>
                                            </button>
                                        </>
                                    )}

                                    <div className="h-px bg-white/5 my-1 mx-3"></div>

                                    {username && (
                                        <button onClick={() => { if (onPasswordChange) onPasswordChange(); setIsProfileOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-all group">
                                            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                                                <i className="fas fa-key text-sm text-slate-400 group-hover:text-slate-300"></i>
                                            </div>
                                            <span className="font-medium">비밀번호 변경</span>
                                        </button>
                                    )}
                                    <button onClick={() => { if (onShowTerms) onShowTerms(); setIsProfileOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-all group">
                                        <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                                            <i className="fas fa-file-contract text-sm text-slate-400 group-hover:text-slate-300"></i>
                                        </div>
                                        <span className="font-medium">이용약관</span>
                                    </button>

                                    <div className="h-px bg-white/5 my-1 mx-3"></div>

                                    {username ? (
                                        <button onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-all group">
                                            <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                                                <i className="fas fa-sign-out-alt text-sm text-red-400"></i>
                                            </div>
                                            <span className="font-medium">로그아웃</span>
                                        </button>
                                    ) : (
                                        <button onClick={() => handleNavigate('/login')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-cyan-400 hover:bg-cyan-500/10 transition-all group">
                                            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                                                <i className="fas fa-sign-in-alt text-sm text-cyan-400"></i>
                                            </div>
                                            <span className="font-medium">로그인 / 회원가입</span>
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div >


            <MessagePanel
                isOpen={isMessagePanelOpen}
                onClose={() => setIsMessagePanelOpen(false)}
                messages={messages}
                onMarkAsRead={handleMarkAsRead}
                onMarkAllAsRead={handleMarkAllAsRead}
            />

            {/* Development Setup Guide Modal */}
            <DevSetupModal
                isOpen={isDevSetupOpen}
                onClose={() => setIsDevSetupOpen(false)}
            />

            {/* LUNA Guide Modal */}
            <LunaGuideModal
                isOpen={isLunaGuideOpen}
                onClose={() => setIsLunaGuideOpen(false)}
            />
        </header >
    );
}
